import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ILoginPinFrequencyModel } from "@/Common/Api/Employee/ILoginFrequencyModel";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PinFrequencyHelper } from "@/Common/Helpers/PinFrequencyHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Guid } from "@/Common/Models/Guid";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { Person } from "@/Common/Models/Person/Person";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { PinFrequencyDocumentType } from "@/Common/Models/UI/Enums";
import { ILoginErrorMessage } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { isRouteMatchingLocation, RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { SessionStore } from "@/Globals/Stores/SessionStore";
import { relativeStore } from "@/Relative/Stores/RelativeStore";

class SessionHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalOperations.Login.OnLoginSubmit, this.onLoginSubmit);
        this.RegisterOperation(LocalOperations.Login.OnLogout, this.onLogout);
        this.RegisterOperation(LocalOperations.Login.RestoreSession, this.restoreSession);
        this.RegisterOperation(LocalOperations.Login.Username_TextChange, this.usernameTextChange);
        this.RegisterOperation(LocalOperations.Login.Password_TextChange, this.passwordTextChange);
        this.RegisterOperation(LocalOperations.Login.AuthenticatorCode_TextChange, this.authenticatorCodeTextChange);
        this.RegisterOperation(LocalOperations.Login.CloseRequestResetPasswordControl, this.closeRequestResetPasswordControl);
        this.RegisterOperation(LocalOperations.Login.OpenRequestResetPasswordControl, this.openRequestResetPasswordControl);
        this.RegisterOperation(LocalOperations.Login.RequestResetPassword, this.requestResetPassword);
        this.RegisterOperation(LocalOperations.Login.ShowPrivacyPolicy, this.togglePrivacyPolicy);
        this.RegisterOperation(LocalOperations.Login.ClosePrivacyPolicy, this.togglePrivacyPolicy);
        this.RegisterOperation(LocalOperations.Login.CheckAgreementAccept, this.checkAgreementAccept);
        this.RegisterOperation(LocalOperations.Login.AcceptAgreement, this.acceptAgreement);
        this.RegisterOperation(LocalOperations.Login.UpdateSignatureByteArrayAndPinFrequency, this.updateSignatureByteArrayAndPinFrequency);
        this.RegisterOperation(LocalOperations.Login.UpdatePinFrequency, this.updatePinFrequency);
        this.RegisterOperation(LocalOperations.Login.RedirectToReloginPage, this.redirectToReloginPage);
        this.RegisterOperation(LocalOperations.Login.AddEmailSuccess, this.handleAddEmailSuccess);
        this.RegisterOperation(LocalOperations.Login.AuthenticationModuleGetAccessInfo, this.authenticationModuleGetAccessInfo);
        this.RegisterOperation(LocalOperations.Person.LoadFile, this.loadFile);
        this.RegisterOperation(LocalOperations.Person.OpenFile, this.openFile);
        this.RegisterOperation(LocalOperations.Person.CloseFile, this.closeFile, false);
    }

    private onLoginSubmit(request: LocalEvent, store: SessionStore): void {
        if ($.trim(store.clientLoginModel.get("UserName")).length == 0) {
            var e: ILoginErrorMessage = { UseLocalResource: true, Message: "UserNameRequiredLabel" };
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
        }
        else if ($.trim(store.clientLoginModel.get("Password")).length == 0) {
            var e: ILoginErrorMessage = { UseLocalResource: true, Message: "PasswordRequiredLabel" };
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
        }
        else {
            var e: ILoginErrorMessage = { UseLocalResource: false, Message: "" };
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
            store.clientLoginModel = store.clientLoginModel.set("ValidatingCredentials", true);
            store.action.login(store.clientLoginModel);
        }
    }

    private onLogout(request: LocalEvent, store: SessionStore): void {
        documentsStore.DocumentsModel.get("DocumentWindowToBeClosedOnLogOut")?.close();

        store.action.logout(false);
    }

    private restoreSession(request: LocalEvent, store: SessionStore): void {
        if (store.lastValidUserName !== store.clientLoginModel.get("UserName")) {
            MyleHelper.redirectToLogin();
        }

        if ($.trim(store.clientLoginModel.get("Password")).length == 0) {
            var e: ILoginErrorMessage = { UseLocalResource: true, Message: "PasswordRequiredLabel" };
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
        }
        else {
            var e: ILoginErrorMessage = { UseLocalResource: false, Message: "" };
            store.clientLoginModel = store.clientLoginModel.set("ErrorMessage", e);
            store.clientLoginModel = store.clientLoginModel.set("ValidatingCredentials", true);
            store.action.resume(store.clientLoginModel);
        }
    }

    private usernameTextChange(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("UserName", request.Get<string>("UserName"));
    }

    private passwordTextChange(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("Password", request.Get<string>("Password"));
    }

    private authenticatorCodeTextChange(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("AuthenticatorCode", request.Get<string>("Value"));
    }

    private closeRequestResetPasswordControl(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("OpenRequestResetPasswordControl", false);
        store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordSuccessful", false);
        store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordErrorMessage", null);
    }

    private openRequestResetPasswordControl(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("OpenRequestResetPasswordControl", true);
    }

    private requestResetPassword(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("RequestResetPasswordInProgress", true);

        let emailAddress = request.Get<string>("emailAddress");
        let language = request.Get<string>("language");
        store.action.sendPasswordReset(emailAddress, language);
    }

    private togglePrivacyPolicy(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("ShowPrivacyPolicy", !store.clientLoginModel.get("ShowPrivacyPolicy"));
    }

    private checkAgreementAccept(request: LocalEvent, store: SessionStore): void {
        var checked = request.Get<boolean>("checked");
        store.clientLoginModel = store.clientLoginModel.set("AcceptAgreementChecked", checked);
    }

    private acceptAgreement(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("AcceptingAgreementInProgress", true);
        store.action.acceptAgreement();
    }

    private handleAddEmailSuccess(request: LocalEvent, store: SessionStore): void {
        store.currentlyAddedEmail = request.Get<string>("emailAddress");
        store.currentlyAddedContactPointId = request.Get<string>("contactPointId");
    }

    private loadFile(request: LocalEvent): void {
        const contactId = request.Get<Guid>("contactId");
        const contactFacetType = request.Get<ContactFacetType>("contactFacetType");

        console.debug("[ActivePersonFile] Load file", contactId);

        switch (contactFacetType) {
            case ContactFacetType.Relative:
                setTimeout(() => relativeStore.actions.loadFile(contactId), 0);
                break;
            case ContactFacetType.Patient:
                patientStore.setActivePatientUI(contactId);
                break;
        }
    }

    private openFile(request: LocalEvent, store: SessionStore): void {
        const person = request.Get<Person>("person");

        console.debug("[ActivePersonFile] Open file", person);

        if (!person)
            return;

        store.selectedFile = person;
        store.selectedFilesHistory = store.selectedFilesHistory.filter(file => file.ContactId !== person.ContactId);
        store.selectedFilesHistory.push(person);

        switch (person.ContactFacetType) {
            case ContactFacetType.Relative:
                if (relativeStore.selectedPersonContactId !== person.ContactId)
                    setTimeout(() => relativeStore.actions.openFile(person.ContactId), 0);
                break;
            case ContactFacetType.Patient:
                if (patientStore.getCurrentPatientContactId() !== person.ContactId)
                    patientStore.setActivePatientUI(person.ContactId);
                break;
        }

        if (!isRouteMatchingLocation("patients")) {
            RouterHistory.push("patients");
        }

        store.EmitChangeToListeners();
    }

    private closeFile(request: LocalEvent, store: SessionStore): void {
        const contactId = request.Get<string>("contactId");
        const redirect = request.Get<boolean>("redirect") ?? true;

        console.debug("[ActivePersonFile] Close file", contactId);

        let closed: Person = null;
        let next: Person = null;

        if (contactId && contactId !== store.selectedFile?.ContactId) {
            // Closing a file not active
            closed = store.selectedFilesHistory.find(x => x.ContactId === contactId);
            store.selectedFilesHistory = store.selectedFilesHistory.filter(x => x.ContactId !== contactId);
        }
        else {
            // Closing the active file
            closed = store.selectedFile;
            store.selectedFilesHistory.pop();
            store.selectedFile = null;
            if (SecondScreenNoteService.getInstance().isChildWindowOpen()) {
                SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({ patientId: null }));
            }
            if (store.selectedFilesHistory.length > 0 && redirect) {
                next = store.selectedFilesHistory[store.selectedFilesHistory.length - 1];
            }
        }

        if (!closed) {
            return;
        }

        if (!next) {
            cacheStore.UpdateTab(MyleTabKey.Patients);
            store.EmitChangeToListeners();
        }

        switch (closed.ContactFacetType) {
            case ContactFacetType.Relative:
                setTimeout(() => relativeStore.actions.closeFile(next === null), 0);
                break;
            case ContactFacetType.Patient:
                store.dispatch(LocalPatientOperations.UI.CloseActivePatientFile, { contactId: closed.ContactId, redirect: next === null }, 0);
                break;
        }

        if (next)
            store.dispatch(LocalOperations.Person.OpenFile, { person: next }, 50);
    }

    private updateSignatureByteArrayAndPinFrequency(request: LocalEvent, store: SessionStore): void {
        const signatureBase64 = request.Get<string>("signatureBase64");
        const loginPinFrequency = request.Get<ILoginPinFrequencyModel>("loginPinFrequency");
        const isSignatureRemoved = request.Get<boolean>("isSignatureRemoved");
        const shouldUpdateSignature = !!signatureBase64;

        store.updateCurrentEmployee(employee => {
            employee.LoginPinFrequency = loginPinFrequency;

            if (shouldUpdateSignature || isSignatureRemoved)
                employee.SignatureByteArray = signatureBase64;

            store.hasPinSet = true;
            store.signOnPrintAccess = !!employee.SignatureByteArray;
        });
    }

    private updatePinFrequency(request: LocalEvent, store: SessionStore): void {
        const pinFrequencyDocumentType = request.Get<PinFrequencyDocumentType>("pinFrequencyDocumentType");
        const token = request.Get<string>("token");

        store.updateCurrentEmployee((p: Employee) => {
            const frequencyModel = LoginPinFrequencyModel.fromResponseModel(p.LoginPinFrequency);
            PinFrequencyHelper.setFrequencyToken(frequencyModel, pinFrequencyDocumentType, token);
            p.LoginPinFrequency = LoginPinFrequencyModel.toResponseModel(frequencyModel);
        });
    }

    private redirectToReloginPage(request: LocalEvent, store: SessionStore): void {
        store.clientLoginModel = store.clientLoginModel.set("SignInRequired", true);
        RouterHistory.push("login");
    }

    private authenticationModuleGetAccessInfo(request: LocalEvent, store: SessionStore): void {
        store.action.getAccessInfo();
    }
}

export { SessionHandler };
